import { useState } from "react";
import { motion } from "framer-motion";
import { Sun, Moon, FlipHorizontal2 } from "lucide-react";

const SISTERS = [
    {
        id: "pearl",
        testid: "mythology-card-pearl",
        icon: Sun,
        name: "The Luminous Sister of Pearl",
        accent: "text-silver",
        ring: "hover:border-silver/50 hover:shadow-[0_0_40px_rgba(226,232,240,0.2)]",
        body: "She is the sister of light, creation, and realized possibilities. She walks through glowing doorways you only glimpsed in dreams — and every threshold she crosses was first drawn in the outline of your own life.",
        secret: "Turn the mirror: her light is not stolen. It is borrowed — and every debt of radiance she owes you is gathering interest on the other side of the veil.",
    },
    {
        id: "shadow",
        testid: "mythology-card-shadow",
        icon: Moon,
        name: "The Obsidian Sister of Shadow",
        accent: "text-[#c77dff]",
        ring: "hover:border-[#c77dff]/50 hover:shadow-[0_0_40px_rgba(199,125,255,0.3)]",
        body: "She is the sister of memory, secret paths, and untaken doorways. She keeps the versions of you that were punished into silence — the talents you buried, the words you swallowed, the power you were taught to fear.",
        secret: "Turn the mirror: her darkness is not a threat. It is a vault. Everything you lost is still alive inside her, waiting for you to remember the way back.",
    },
];

export const Mythology = () => {
    const [mirrored, setMirrored] = useState(false);

    return (
        <section id="mythology" data-testid="mythology-section" className="relative px-5 py-28 sm:px-8 lg:py-36">
            <div className="nebula-blob left-[5%] top-[10%] h-96 w-96 bg-[#5a189a]/20" />
            <div className="mx-auto max-w-6xl">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9 }}
                    className="mb-16 max-w-2xl"
                >
                    <p className="eyebrow mb-4">Chapter I · The Mythology</p>
                    <h2 className="text-glow-lilac font-display text-2xl font-bold tracking-wide text-[#f8f7ff] sm:text-3xl lg:text-4xl">
                        TWO SISTERS. ONE SOUL. A CIRCLE WITH NO SEAM.
                    </h2>
                    <p className="mt-6 font-cormorant text-lg italic leading-relaxed text-[#e0aaff]/80">
                        They face one another across the thinnest boundary in existence — the surface of a mirror. Each
                        carries a fragment of the other. No one has ever found the place where one sister ends and the
                        other begins.
                    </p>
                </motion.div>

                <div className="grid gap-8 md:grid-cols-2">
                    {SISTERS.map((s, i) => (
                        <motion.article
                            key={s.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.9, delay: i * 0.2 }}
                            data-testid={s.testid}
                            className={`glass-card glass-card-hover relative overflow-hidden p-8 sm:p-10 ${s.ring}`}
                        >
                            <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-40" />
                            <s.icon className={`mb-6 h-8 w-8 ${s.accent}`} />
                            <h3 className="font-cinzel text-xl font-semibold tracking-wide text-[#f8f7ff] sm:text-2xl">
                                {s.name}
                            </h3>
                            <p className="mt-5 font-cormorant text-lg leading-relaxed text-[#e0aaff]/80">
                                {mirrored ? s.secret : s.body}
                            </p>
                        </motion.article>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="mt-12 flex justify-center"
                >
                    <button
                        type="button"
                        data-testid="mythology-mirror-toggle"
                        onClick={() => setMirrored((m) => !m)}
                        className="btn-ghost-glow flex items-center gap-3"
                    >
                        <FlipHorizontal2 className={`h-4 w-4 transition-transform duration-500 ${mirrored ? "-scale-x-100" : ""}`} />
                        {mirrored ? "RESTORE THE REFLECTION" : "TURN THE MIRROR"}
                    </button>
                </motion.div>
            </div>
        </section>
    );
};
